import { Controller, useForm } from "react-hook-form";
import React, { useEffect, useState } from "react";
import "./CreateTicketTemplateModal.scss";
import Textarea, {
  type TextareaErrorProps,
} from "../../../Controls/Textarea/Textarea";
import {
  TextButton,
  TextInput,
  ModalButtonsContainer,
  REQUIRED_ERROR_MESSAGE,
} from "@SeemplicityDev/shared";
import { useMutation } from "@apollo/client";
import { CREATE_TICKET_TEMPLATE } from "../../TicketingAPI.js";
import TicketTemplatePreview from "../TicketTemplatePreview/TicketTemplatePreview.js";
import type { TemplateField } from "../TicketTemplatePreview/TicketTemplatePreview";

interface TicketTemplateSummary {
  name: string;
}

interface CreateTicketTemplateSuccessPayload {
  name: string;
  id: string | undefined;
  fields: Array<Record<string, unknown>>;
}

interface CreateTicketTemplateFormValues {
  templateName: string;
  templateDescription: string;
}

export interface CreateTicketTemplateModalProps {
  keys: unknown;
  templates?: TicketTemplateSummary[];
  fields?: TemplateField[];
  ticketProviderId: string;
  onSuccess: (payload: CreateTicketTemplateSuccessPayload) => void;
  onError: (error: { message: string }) => void;
  closeModal: () => void;
  onEventTriggered: (eventName: string) => void;
}

const CreateTicketTemplateModal: React.FC<CreateTicketTemplateModalProps> = ({
  keys,
  templates,
  fields,
  ticketProviderId,
  onSuccess,
  onError,
  closeModal,
  onEventTriggered,
}) => {
  const methods = useForm<CreateTicketTemplateFormValues>({
    mode: "all",
  });
  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty, errors },
  } = methods;
  const [createTicketTemplate] = useMutation(CREATE_TICKET_TEMPLATE);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [templateFields, setTemplateFields] = useState<TemplateField[]>([]);

  useEffect(() => {
    if (!!fields) {
      setTemplateFields(fields);
    }
  }, [fields]);

  const deleteField = (key: string) => {
    setTemplateFields(templateFields.filter((field) => field.key !== key));
  };

  const onSubmit = (formData: CreateTicketTemplateFormValues) => {
    onEventTriggered("Ticket_Templates_Template_Saved");
    const formattedFields = templateFields.map((field) => ({
      field_name: field.key,
      display_name: field.label,
      value: JSON.stringify(field.value),
      field_type: field.fieldType,
    }));

    createTicketTemplate({
      variables: {
        name: formData.templateName,
        description: formData.templateDescription,
        ticket_provider_id: ticketProviderId,
        fields: formattedFields,
        selected_keys: JSON.stringify(keys),
      },
    })
      .then((res) => {
        onSuccess({
          name: formData.templateName,
          id: res.data?.create_ticket_template?.ticket_template?.id,
          fields: templateFields.map((field) => ({
            ...field,
            value: JSON.stringify(field.value),
            isCompatible: true,
          })),
        });
        closeModal();
      })
      .catch((err) => {
        onError({ message: err.message });
      })
      .finally(() => setIsSubmittingForm(false));
  };

  return (
    <div className={"create-ticket-template-modal-wrapper"}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={"modal-scroll-area"}>
          <Controller
            name={"templateName"}
            render={({ field: { value, onChange, onBlur } }) => {
              return (
                <TextInput
                  type={"text"}
                  value={value}
                  data-testid={"templateName"}
                  labelTop
                  onBlur={onBlur}
                  name={"templateName"}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    onChange(e);
                  }}
                  label={"Name"}
                  placeholder={"Name"}
                  error={errors?.templateName ? errors?.templateName : null}
                  required
                />
              );
            }}
            control={control}
            rules={{
              required: REQUIRED_ERROR_MESSAGE,
              validate: (value) =>
                !templates?.find((t) => t.name === value) ||
                "Name already exists",
            }}
          />
          <Controller
            name={"templateDescription"}
            render={({ field: { value, onChange, onBlur } }) => {
              return (
                <Textarea
                  type={"text"}
                  value={value}
                  data-testid={"templateDescription"}
                  labelTop
                  onBlur={onBlur}
                  name={"templateDescription"}
                  onChange={(e) => {
                    onChange(e);
                  }}
                  label={"Description"}
                  placeholder={"Enter a meaningful description"}
                  rowNumber={3}
                  error={
                    errors?.templateDescription
                      ? (errors?.templateDescription as TextareaErrorProps)
                      : undefined
                  }
                />
              );
            }}
            control={control}
          />
          <div className={"template-preview"}>
            <h4>Template Preview</h4>
            <div className="template-fields-wrap">
              {!!templateFields && (
                <TicketTemplatePreview
                  fields={templateFields}
                  onDeleteField={deleteField}
                />
              )}
            </div>
          </div>
        </div>
        <ModalButtonsContainer>
          <TextButton
            type="button"
            data-testid={"back-to-edit"}
            label={"Back to Edit"}
            data-analytics-event="user_click"
            data-analytics-props={`{"name": "back_to_edit_btn", "container": "create_template_modal"}`}
            onClick={() => {
              closeModal();
            }}
          />
          <TextButton
            type="submit"
            label={"Save as Template"}
            data-testid={"save-template"}
            buttonStyle={"primary"}
            rightIcon={isSubmittingForm ? "seem-icon-spinner spinner" : ""}
            isSubmitting={isSubmittingForm}
            data-analytics-event="user_click"
            data-analytics-props={`{"name": "save_as_template_btn", "container": "create_template_modal"}`}
            disabled={
              templateFields.length === 0 ||
              !isValid ||
              !isDirty ||
              isSubmittingForm
            }
          />
        </ModalButtonsContainer>
      </form>
    </div>
  );
};
export default CreateTicketTemplateModal;
